/**
 * "A fight is ready" — the quiet cue that sits above the storyboard.
 *
 * Deliberately understated. Combat is prepared speculatively and most of what
 * gets prepared is never used, so this must not read as an instruction or an
 * interruption. It is one more prepared consequence, sitting where the GM
 * already looks.
 *
 * It says what the fight is, how long it should run, and — when the party was
 * guessed at rather than known — that the numbers are rough. A GM who is told
 * "hard" about a party the system never saw should know that.
 */
import { useState } from 'react';

import type { Encounter } from '@/api/combat';
import type { DiscardKind } from '@/api/client';

interface Props {
  encounter: Encounter;
  onOpen: () => void;
  onDiscard: (refusal?: { kind?: DiscardKind; note?: string }) => void;
  /** The GM said build it. Starts the two-minute build the pitch was gating. */
  onAccept: () => void;
}

export function EncounterCue({ encounter, onOpen, onDiscard, onAccept }: Props) {
  // Before anything is built there is a pitch: a few sentences describing the
  // fight, and two answers. A GM told the fast provisional build "doesn't do
  // anything" — they were going to wait for the real fight either way, so what
  // they needed first was a sentence and a way to say no.
  if (encounter.status === 'pitched') {
    return <PitchCue encounter={encounter} onAccept={onAccept} onDiscard={onDiscard} />;
  }
  if (encounter.status === 'building') {
    // Accepting keeps the pitch in the row, but this card used to show only
    // the title — so the moment a GM said yes, the description they said yes
    // to vanished for the whole two-minute build. Requested fights have no
    // pitch; for those the title is the GM's own words and stands alone.
    const pitch = encounter.pitch;
    return (
      <section className="cue">
        <div className="cue__body">
          <span className="cue__eyebrow">Building</span>
          <h3 className="cue__title">{encounter.title || 'Your fight'}</h3>
          {pitch?.pitch ? <p className="cue__pitch">{pitch.pitch}</p> : null}
          {pitch?.opposition ? (
            <p className="cue__meta">
              <span>{pitch.opposition}</span>
            </p>
          ) : null}
          <p className="cue__meta">
            Terrain, stat blocks for every side, and a clock. About two minutes.
          </p>
        </div>
      </section>
    );
  }

  const unscored = encounter.scored_difficulty === null;
  // A scored difficulty computed against a guessed party is real arithmetic on
  // invented input, and it reads identically to the real thing unless it is
  // labelled. This component's docstring claimed to do that long before it did.
  const guessedParty = !unscored && encounter.party_estimated;
  const creatures = encounter.design?.creatures ?? [];
  const roster = creatures
    .map((c) => (c.count > 1 ? `${c.count} ${c.name}` : c.name))
    .join(', ');

  return (
    <section className="cue">
      <div className="cue__body">
        <span className="cue__eyebrow">If this turns into a fight</span>
        <h3 className="cue__title">{encounter.title || 'Encounter ready'}</h3>
        {roster ? <p className="cue__roster">{roster}</p> : null}
        <p className="cue__meta">
          {unscored ? (
            <span className="badge badge--warn">difficulty unverified</span>
          ) : (
            <span className={guessedParty ? 'badge badge--warn' : 'badge badge--ok'}>
              {encounter.scored_difficulty}
              {encounter.adjusted_xp ? ` · ${encounter.adjusted_xp.toLocaleString()} XP` : ''}
              {guessedParty ? ' · for a guessed party' : ''}
            </span>
          )}
          {encounter.round_budget ? <span>~{encounter.round_budget} rounds</span> : null}
        </p>
        {encounter.party_line ? (
          <p className="cue__scored">Sized for {encounter.party_line}</p>
        ) : null}
        {guessedParty ? (
          <p className="cue__note">
            {encounter.party_estimate_reason || 'No character levels on file'}, so this was
            scored against a stand-in party. Add your party in campaign setup and the next
            fight is scored against the real one.
          </p>
        ) : null}
      </div>
      <div className="cue__actions">
        <button type="button" className="button button--primary" onClick={onOpen}>
          Open
        </button>
        <RefuseButton key={encounter.id} onDiscard={onDiscard} />
      </div>
    </section>
  );
}

/**
 * The fight, described, before it is built.
 *
 * Two taps and no third option. "Maybe" is not useful here — the GM is between
 * sentences at a table, and the cost of the wrong fight is that they read a
 * board that does not match what is happening. Refusing is cheap; the pitch
 * cost a fraction of a cent and nothing else has been spent yet.
 */
function PitchCue({
  encounter,
  onAccept,
  onDiscard,
}: {
  encounter: Encounter;
  onAccept: () => void;
  onDiscard: (refusal?: { kind?: DiscardKind; note?: string }) => void;
}) {
  const pitch = encounter.pitch;
  return (
    <section className="cue cue--pitch">
      <div className="cue__body">
        <span className="cue__eyebrow">A fight, if you want it</span>
        <h3 className="cue__title">{encounter.title || 'Untitled fight'}</h3>
        {pitch?.build_error ? (
          // A build the GM already approved died and the pitch came back.
          // Silence here is what turned one failure into a vanished fight in
          // a live session — the GM must see that tapping again is a retry.
          <p className="cue__note cue__note--error">
            The last build didn’t finish. Press Build it to try again, or Not
            this to change the encounter.
          </p>
        ) : null}
        {pitch?.pitch ? <p className="cue__pitch">{pitch.pitch}</p> : null}
        <p className="cue__meta">
          {pitch?.opposition ? <span>{pitch.opposition}</span> : null}
          {pitch?.why_now ? <span className="cue__why">{pitch.why_now}</span> : null}
        </p>
        {/* Who it will be sized for, said before the GM accepts. A real
            session asked for a Parshendi ambush across a chasm and got one
            CR 1/8 and one CR 0, because the stored party was a single level-1
            character. The arithmetic was right and nothing showed the input. */}
        {encounter.party_line ? (
          <p className="cue__scored">Sized for {encounter.party_line}</p>
        ) : null}
      </div>
      <div className="cue__actions">
        <button type="button" className="button button--primary" onClick={onAccept}>
          Build it
        </button>
        <RefuseButton key={encounter.id} onDiscard={onDiscard} />
      </div>
    </section>
  );
}

/**
 * "Not this" — and then, what instead?
 *
 * The button used to throw the fight away and record nothing, which made a
 * refusal worth exactly as much to the system as a fight nobody clicked on.
 * In one thirty-minute session a GM was pitched four fights, refused three —
 * two of them inside ten seconds — and ended the night with no combat at all,
 * because there was no path from "no" to a better offer. His words: it kept
 * offering combats when it didn't make sense for them to show up.
 *
 * Three answers, because three different things were arriving as one. Saying
 * what you want instead is a spec the agent builds on its next tick; the
 * other two stop the speculative watcher guessing, for ten minutes or for the
 * night. All three are one tap away and none of them is required — the panel
 * closes on Escape and nothing is refused until a button is pressed.
 */
function RefuseButton({
  onDiscard,
}: {
  onDiscard: (refusal?: { kind?: DiscardKind; note?: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');

  if (!open) {
    return (
      <button
        type="button"
        className="button button--ghost"
        onClick={() => setOpen(true)}
        title="The table went somewhere else"
      >
        Not this
      </button>
    );
  }

  const send = () => {
    const said = note.trim();
    if (!said) return;
    onDiscard({ kind: 'wrong_fight', note: said });
  };

  return (
    <div className="refuse">
      <div className="refuse__row">
        <input
          className="refuse__input"
          autoFocus
          value={note}
          placeholder="What's actually coming?"
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') send();
            // At a table, mid-sentence. Escape has to put the board back the
            // way it was without refusing anything.
            if (e.key === 'Escape') setOpen(false);
          }}
        />
        <button
          type="button"
          className="button button--primary"
          disabled={!note.trim()}
          onClick={send}
        >
          That instead
        </button>
      </div>
      <div className="refuse__row refuse__row--alts">
        <button
          type="button"
          className="button button--ghost button--small"
          onClick={() => onDiscard({ kind: 'not_now' })}
        >
          Not right now
        </button>
        <button
          type="button"
          className="button button--ghost button--small"
          onClick={() => onDiscard({ kind: 'no_combat' })}
        >
          No fights tonight
        </button>
        <button
          type="button"
          className="button button--ghost button--small"
          onClick={() => setOpen(false)}
        >
          Never mind
        </button>
      </div>
    </div>
  );
}
